import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AuthState, LoginResponse } from '../types';

// Action types
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Reducer function
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

// Context interface
interface AuthContextType extends AuthState {
  login: (credential: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is already logged in on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('maksimer_user');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        dispatch({ type: 'LOGIN_SUCCESS', payload: user });
      } catch (error) {
        localStorage.removeItem('maksimer_user');
      }
    }
  }, []);

  // Login function
  const login = async (credential: string) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      // Decode the JWT credential
      const decoded = JSON.parse(atob(credential.split('.')[1]));
      
      // Validate email domain
      const email = decoded.email;
      if (!email) {
        throw new Error('E-postadresse ikke funnet i Google-konto');
      }

      const domain = email.split('@')[1];
      if (domain !== 'maksimer.com' && domain !== 'maksimer.no') {
        throw new Error('Kun brukere med maksimer.com eller maksimer.no e-postadresser kan logge inn');
      }

      // Create user object
      const user: User = {
        id: decoded.sub,
        email: email,
        name: decoded.name || 'Ukjent bruker',
        picture: decoded.picture || '',
        domain: domain as 'maksimer.com' | 'maksimer.no',
      };

      // Note: We don't need to save access token for Google Sheets API
      // as we're using service account authentication instead

      // Save to localStorage
      localStorage.setItem('maksimer_user', JSON.stringify(user));
      
      dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Innlogging mislyktes';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('maksimer_user');
    dispatch({ type: 'LOGOUT' });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
